import { useNavigate } from "@tanstack/solid-router";
import { For, Show, createSignal } from "solid-js";

import { apiRequest } from "~/lib/api";
import { useContacts, useMergeContact } from "~/lib/hooks";
import type { ContactRead } from "~/lib/types";

type Props = {
  wsKey: () => string;
  doomed: ContactRead;
  onClose: () => void;
};

export function MergeContactDialog(props: Props) {
  const navigate = useNavigate();
  const mergeMutation = useMergeContact(props.wsKey);

  const [query, setQuery] = createSignal("");
  const [survivor, setSurvivor] = createSignal<ContactRead | null>(null);
  const [keepLinkedUserId, setKeepLinkedUserId] = createSignal<string | null>(null);
  const [err, setErr] = createSignal<string | null>(null);

  const search = useContacts(props.wsKey, () => ({
    q: query() || undefined,
    limit: 12,
  }));

  // Only required when both contacts have different non-null linked_user_id.
  const linkedUserConflict = () => {
    const s = survivor();
    if (!s) return false;
    return !!(
      s.linked_user_id &&
      props.doomed.linked_user_id &&
      s.linked_user_id !== props.doomed.linked_user_id
    );
  };

  const handleConfirm = async () => {
    const target = survivor();
    if (!target) return;
    if (linkedUserConflict() && !keepLinkedUserId()) {
      setErr("Pick which linked user to keep — both contacts are linked to a user.");
      return;
    }
    setErr(null);
    try {
      await mergeMutation.mutateAsync({
        doomedConId: props.doomed.id,
        intoContactId: target.id,
        keepLinkedUserId: linkedUserConflict() ? keepLinkedUserId() : undefined,
      });
      props.onClose();
      void navigate({
        to: "/workspaces/$wsKey/contacts/$conId",
        params: { wsKey: props.wsKey(), conId: target.id },
      });
    } catch (e) {
      setErr((e as Error).message);
    }
  };

  const doomedLabel = () =>
    props.doomed.display_name ?? props.doomed.full_name ?? props.doomed.primary_email ?? "this contact";

  return (
    <dialog open class="modal modal-open" aria-modal="true" aria-labelledby="merge-con-title">
      <div class="modal-box max-w-2xl">
        <h3 id="merge-con-title" class="font-bold text-lg mb-2">
          Merge "{doomedLabel()}" into…
        </h3>
        <p class="text-sm opacity-80 mb-3">
          The contact you're viewing will be archived; its notes, activities, deals,
          tags, and account associations move to the contact you pick.
        </p>

        <Show
          when={survivor()}
          fallback={
            <>
              <input
                type="search"
                class="input input-bordered w-full"
                placeholder="Search contacts by name, email, phone…"
                aria-label="Search contacts to merge into"
                value={query()}
                onInput={(e) => setQuery(e.currentTarget.value)}
                autofocus
              />
              <ul class="mt-2 max-h-64 overflow-y-auto divide-y divide-base-300">
                <For each={(search.data?.items ?? []).filter((c) => c.id !== props.doomed.id)}>
                  {(c) => (
                    <li>
                      <button
                        type="button"
                        class="w-full text-left px-3 py-2 hover:bg-base-200"
                        onClick={async () => {
                          try {
                            const full = await apiRequest<ContactRead>(
                              `/v1/workspaces/${props.wsKey()}/contacts/${c.id}`,
                            );
                            setSurvivor(full);
                          } catch (e) {
                            setErr((e as Error).message);
                          }
                        }}
                      >
                        <div class="font-medium">
                          {c.display_name ?? c.full_name ?? "Unnamed"}
                        </div>
                        <div class="text-xs opacity-60 truncate">
                          {[c.primary_email, c.lifecycle_stage].filter(Boolean).join(" · ") || "—"}
                        </div>
                      </button>
                    </li>
                  )}
                </For>
                <Show when={search.data && search.data.items.length === 0}>
                  <li class="px-3 py-3 text-sm opacity-60 text-center">
                    No contacts match.
                  </li>
                </Show>
              </ul>
            </>
          }
        >
          {(targetCon) => (
            <div>
              <div class="grid grid-cols-2 gap-3 text-sm">
                <div class="bg-error/10 rounded-lg p-3">
                  <div class="text-xs uppercase opacity-60 mb-1">Going away</div>
                  <div class="font-medium">{doomedLabel()}</div>
                  <DiffRow label="Email" value={props.doomed.primary_email} />
                  <DiffRow label="Phone" value={props.doomed.primary_phone} />
                  <DiffRow label="Lifecycle" value={props.doomed.lifecycle_stage} />
                  <DiffRow
                    label="Linked user"
                    value={props.doomed.linked_user_id ? "set" : null}
                  />
                </div>
                <div class="bg-success/10 rounded-lg p-3">
                  <div class="text-xs uppercase opacity-60 mb-1">Keeping</div>
                  <div class="font-medium">
                    {targetCon().display_name ?? targetCon().full_name}
                  </div>
                  <DiffRow label="Email" value={targetCon().primary_email} />
                  <DiffRow label="Phone" value={targetCon().primary_phone} />
                  <DiffRow label="Lifecycle" value={targetCon().lifecycle_stage} />
                  <DiffRow
                    label="Linked user"
                    value={targetCon().linked_user_id ? "set" : null}
                  />
                </div>
              </div>

              <Show when={linkedUserConflict()}>
                <div class="mt-4 p-3 rounded-lg bg-warning/10 border border-warning/30">
                  <div class="text-sm font-medium mb-2">
                    Both contacts are linked to a different user. Pick which to keep.
                  </div>
                  <div class="flex flex-col gap-2 text-sm">
                    <label class="cursor-pointer flex items-center gap-2">
                      <input
                        type="radio"
                        name="keep_linked_user"
                        class="radio radio-sm"
                        checked={keepLinkedUserId() === survivor()!.linked_user_id}
                        onChange={() => setKeepLinkedUserId(survivor()!.linked_user_id ?? null)}
                      />
                      Keep the surviving contact's linked user
                    </label>
                    <label class="cursor-pointer flex items-center gap-2">
                      <input
                        type="radio"
                        name="keep_linked_user"
                        class="radio radio-sm"
                        checked={keepLinkedUserId() === props.doomed.linked_user_id}
                        onChange={() => setKeepLinkedUserId(props.doomed.linked_user_id ?? null)}
                      />
                      Keep the going-away contact's linked user
                    </label>
                  </div>
                </div>
              </Show>

              <Show when={err()}>
                <div role="alert" class="alert alert-error text-sm mt-3">
                  {err()}
                </div>
              </Show>
            </div>
          )}
        </Show>

        <div class="modal-action">
          <button
            type="button"
            class="btn btn-ghost"
            onClick={props.onClose}
            disabled={mergeMutation.isPending}
          >
            Cancel
          </button>
          <Show when={survivor()}>
            <button
              type="button"
              class="btn btn-ghost"
              onClick={() => {
                setSurvivor(null);
                setKeepLinkedUserId(null);
              }}
              disabled={mergeMutation.isPending}
            >
              Back
            </button>
            <button
              type="button"
              class="btn btn-warning"
              onClick={handleConfirm}
              disabled={
                mergeMutation.isPending ||
                (linkedUserConflict() && !keepLinkedUserId())
              }
            >
              {mergeMutation.isPending ? "Merging…" : "Confirm merge"}
            </button>
          </Show>
        </div>
      </div>
    </dialog>
  );
}

function DiffRow(props: { label: string; value: string | null }) {
  return (
    <div class="text-xs mt-1">
      <span class="opacity-60">{props.label}:</span>{" "}
      <span class="font-medium">{props.value ?? "—"}</span>
    </div>
  );
}
